import type { ReferenceImage } from '@historyjki/shared';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { FileUpload } from '../ui/FileUpload';
import { ImageEditor } from './ImageEditor';
import type {
  ImageEditorExtraAction,
  ImageEditorFeatures,
  ImageEditorVariant,
  ImageRefDTO,
} from './types';

interface ImageEditorListProps {
  /** Owning surface — used by the editor to build each item's `image_ref`. */
  ownerType: 'project' | 'story_element';
  ownerId: string;
  projectId: string;
  /** Reference images to render, in display order. */
  images: ReferenceImage[];
  /** Friendly target label per item — formatted as "<itemLabel>". */
  itemLabel: string;
  /** Feature flags applied to every item. */
  features: ImageEditorFeatures;
  /** Optional per-item action factory (e.g. story-element refs use this for
   *  "Generate variation" + "Amend description"). */
  buildExtraActions?: (
    img: ReferenceImage,
  ) => ImageEditorExtraAction[] | undefined;
  /** Per-item callbacks. */
  onGenerate?: (img: ReferenceImage) => void;
  onOpenVariants?: (img: ReferenceImage) => void;
  onCloseVariants?: (img: ReferenceImage) => void;
  onSelectVariant?: (img: ReferenceImage, r2Key: string) => void;
  onRefine?: (img: ReferenceImage) => void;
  onEditPrompt?: (img: ReferenceImage) => void;
  onClickImage?: (img: ReferenceImage, index: number) => void;
  onDelete?: (img: ReferenceImage) => void;
  /** Per-item state for variants pane and busy flags. Each map is keyed by
   *  reference_image id so the parent can drive multiple cards
   *  independently — e.g. one ref's Generate spins while another's
   *  variants pane is open. */
  generatingIds?: Set<string>;
  variantsOpenIds?: Set<string>;
  loadingVariantsIds?: Set<string>;
  variantsByImageId?: Record<string, ImageEditorVariant[]>;
  /** Set of image ids currently being deleted, so per-item buttons can show
   *  loading state without the parent threading it through extra props. */
  deletingIds?: Set<string>;
  /** Optional "Add new" affordances rendered after the gallery grid. */
  onUpload?: (files: File[]) => void;
  uploadLabel?: string;
  /** Optional generate-from-text button next to the upload control. */
  onGenerateNew?: () => void;
  generateNewLabel?: string;
  generateNewLoading?: boolean;
  generateNewDisabled?: boolean;
  /** Empty-state message when `images` is empty. */
  emptyMessage?: string;
}

/**
 * Gallery wrapper around `ImageEditor`. Renders one compact `ImageEditor`
 * card per existing reference image, plus an optional "Add new" panel
 * (file upload + generate-from-text button).
 *
 * Used by `DefineStage` for both the story-element ref gallery and the
 * project style ref gallery. Forwards every per-item editor callback so
 * the parent can decide which features each surface enables — see the
 * `features` prop.
 */
export function ImageEditorList({
  ownerType,
  ownerId,
  projectId,
  images,
  itemLabel,
  features,
  buildExtraActions,
  onGenerate,
  onOpenVariants,
  onCloseVariants,
  onSelectVariant,
  onRefine,
  onEditPrompt,
  onClickImage,
  onDelete,
  generatingIds,
  variantsOpenIds,
  loadingVariantsIds,
  variantsByImageId,
  deletingIds,
  onUpload,
  uploadLabel,
  onGenerateNew,
  generateNewLabel,
  generateNewLoading,
  generateNewDisabled,
  emptyMessage,
}: ImageEditorListProps) {
  return (
    <div className="image-editor-list">
      {images.length === 0 && emptyMessage && (
        <p className="image-editor-list__empty">{emptyMessage}</p>
      )}

      {images.length > 0 && (
        <div className="image-editor-list__grid">
          {images.map((img, idx) => {
            const imageRef: ImageRefDTO = {
              kind: 'reference_image',
              project_id: projectId,
              owner_type: ownerType,
              owner_id: ownerId,
              reference_image_id: img.id,
            };
            return (
              <ImageEditor
                key={img.id}
                size="compact"
                imageRef={imageRef}
                imageR2Key={img.r2_key}
                targetLabel={itemLabel}
                features={features}
                isGenerating={generatingIds?.has(img.id)}
                variantsOpen={variantsOpenIds?.has(img.id)}
                loadingVariants={loadingVariantsIds?.has(img.id)}
                variants={variantsByImageId?.[img.id] ?? []}
                extraActions={buildExtraActions?.(img)}
                onGenerate={onGenerate ? () => onGenerate(img) : undefined}
                onOpenVariants={
                  onOpenVariants ? () => onOpenVariants(img) : undefined
                }
                onCloseVariants={
                  onCloseVariants ? () => onCloseVariants(img) : undefined
                }
                onSelectVariant={
                  onSelectVariant
                    ? (r2Key) => onSelectVariant(img, r2Key)
                    : undefined
                }
                onRefine={onRefine ? () => onRefine(img) : undefined}
                onEditPrompt={
                  onEditPrompt ? () => onEditPrompt(img) : undefined
                }
                onClickImage={
                  onClickImage ? () => onClickImage(img, idx) : undefined
                }
                onDelete={onDelete ? () => onDelete(img) : undefined}
                deleting={deletingIds?.has(img.id)}
              />
            );
          })}
        </div>
      )}

      {(onUpload || onGenerateNew) && (
        <Card padding="md" className="image-editor-list__add">
          {onUpload && (
            <FileUpload
              accept="image/*"
              onFiles={onUpload}
              label={uploadLabel ?? 'Upload reference image'}
              className="image-editor-list__upload"
            />
          )}
          {onGenerateNew && (
            <Button
              variant="secondary"
              size="sm"
              onClick={onGenerateNew}
              loading={generateNewLoading}
              disabled={generateNewDisabled}
            >
              {generateNewLabel ?? 'Generate Reference Image'}
            </Button>
          )}
        </Card>
      )}
    </div>
  );
}
